import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const CommunicationTemplatesContext = createContext();
 
export const useCommunicationTemplates = () => {
  const context = useContext(CommunicationTemplatesContext);
  if (!context) {
    throw new Error('useCommunicationTemplates must be used within a CommunicationTemplatesProvider');
  }
  return context;
};

export const CommunicationTemplatesProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [communicationTemplates, setCommunicationTemplates] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/communication-templates');

  const getCommunicationTemplates = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      setPaginate(true)
      let response = await apiInstance.call('communication-templates?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          if(filters?.active === true){
            query += 'active=1';
          }else{
            query += 'active=0';
          }
         
        }
        if(filters?.recipient_id){
          query += '&recipient_id='+filters?.recipient_id;
        }
        if(filters?.communication_template_category_id){
          query += filters.communication_template_category_id
          .map(id => `&communication_template_category_id[]=${id}`)
          .join('');
           
        }
        response = await apiInstance.call('communication-templates?'+query);  
      }
      
      if (response.status === 200) {
       // setPaginate(response.data.paginate)
       
        setCommunicationTemplates(response?.data?.communicationTemplates || []);
       
        
        if(filters){
          return response.data.communicationTemplates;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch communication templates information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, getToken,paginate]);


  const getCommunicationTemplateAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-communication-templates-additional-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get communication template data'
      });
    } 
  }, [getToken, handleApiError]);

  const getCommunicationTemplateByIdApi = useCallback(async (id, filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`communication-templates/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let communicationTemplates = {'data':[response.data.communicationTemplate]}
      setCommunicationTemplates(communicationTemplates || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/communication-templates/edit/'+id))){
        if (getToken()) {
        
          
          await getCommunicationTemplateByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getCommunicationTemplateByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getCommunicationTemplates(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getCommunicationTemplates, searchParams, initialized,hideLoading,setApi]);

  const addCommunicationTemplate = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
       
      const response = await apiInstance.call('communication-templates', 'POST', data, true, false);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the communication templates list
        await getCommunicationTemplates();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Communication Template'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create communication template'
      });
      return { status: 500, error: error.message };
    }
  };

  const updateCommunicationTemplate = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
        
      const response = await apiInstance.call(`communication-templates/${id}`, 'POST', updatedData, true, false);
      
      if (response.status === 200) {
        // Refresh the communication templates list
        await getCommunicationTemplates();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update communication template'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update communication template'
      });
      return { status: 500, error: error.message };
    }
  };

  const getCommunicationTemplateById = useCallback(async (id,data = null) => {

    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let query = '';
      if(data){
        query = '?module='+data.module+'&module_id='+data.module_id;
        if(data?.filters){
          Object.keys(data.filters).forEach(key => {
            query += '&filters['+key+']='+data.filters[key];
          });
        }
      if(data?.c_module){
          query += '&c_module='+data.c_module;
        }
      }
      const response = await apiInstance.call(`communication-templates/${id}/edit`+query, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.communicationTemplate };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch communication template details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch communication template details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const deleteCommunicationTemplate = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`communication-templates/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the communication templates list
        await getCommunicationTemplates();
        navigate(getRoutePath('/communication-templates'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete communication template'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete communication template'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <CommunicationTemplatesContext.Provider
      value={{
        communicationTemplates,
        addCommunicationTemplate,
        getCommunicationTemplateById,
        updateCommunicationTemplate,
        deleteCommunicationTemplate,
        getCommunicationTemplates,
        formErrors,
        clearErrors,
        setPaginate,
        paginate,
        getCommunicationTemplateAdditionalData
      }}
    >
      {children}
    </CommunicationTemplatesContext.Provider>
  );
}; 